import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import RetailerOfferView from '../components/RetailerOfferView';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  X, 
  Monitor, 
  Smartphone, 
  Eye,
  AlertTriangle
} from 'lucide-react';
import api from '../lib/api';

/**
 * OfferPreviewSandbox - Aperçu visuel Retailer sécurisé (mode snapshot)
 * 
 * Utilise le composant unifié RetailerOfferView en mode="preview"
 * 
 * ARCHITECTURE UNIFIÉE:
 * - Même rendu que OfferDetailPage (Retailer)
 * - Navigation désactivée via mode="preview"
 * - Accès restreint Admin/Partner uniquement
 * 
 * MODES:
 * - /dashboard/offer-preview/:id → Offre existante
 * - /dashboard/offer-preview/new?data=... → Création (données formulaire)
 */
const OfferPreviewSandbox = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [offer, setOffer] = useState(null);
  const [products, setProducts] = useState([]);
  const [partner, setPartner] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('desktop'); // desktop | mobile
  const [productLoadingCase, setProductLoadingCase] = useState(null);
  
  // Vérification des permissions (Admin ou Partner uniquement)
  useEffect(() => {
    if (user && !['admin', 'partenaire'].includes(user.role)) {
      navigate('/dashboard');
    }
  }, [user, navigate]);
  
  // Chargement des données
  useEffect(() => {
    const loadOffer = async () => {
      setLoading(true);
      setError(null);
      
      try {
        if (id === 'new') {
          // Mode création: données depuis URL params
          const dataParam = searchParams.get('data');
          if (dataParam) {
            const formData = JSON.parse(decodeURIComponent(dataParam));
            setOffer({
              ...formData,
              id: 'preview-new',
              created_at: new Date().toISOString()
            });
            // En mode création, pas de produits à charger
            setProducts([]);
            setProductLoadingCase('no_products');
          } else {
            setError('Aucune donnée de prévisualisation');
          }
        } else {
          // Mode édition: chargement depuis API
          const response = await api.get(`/offers/${id}`);
          const foundOffer = response.data;
          setOffer(foundOffer);
          
          // Charger les produits associés
          const hasSpecificProducts = foundOffer.product_ids && foundOffer.product_ids.length > 0;
          const isAllPartnerProducts = foundOffer.apply_to_all_products === true;
          const hasPartnerId = !!foundOffer.partner_id;
          
          if (hasSpecificProducts) {
            setProductLoadingCase('specific_ids');
            try {
              const productsResponse = await api.get('/products?limit=200');
              const rawAll = productsResponse.data || [];
              const allProducts = Array.isArray(rawAll) ? rawAll : (rawAll?.products || []);
              const linkedProducts = allProducts.filter(p => foundOffer.product_ids.includes(p.id));
              setProducts(linkedProducts);
            } catch (err) {
              setProductLoadingCase('error');
            }
          } else if (isAllPartnerProducts && hasPartnerId) {
            setProductLoadingCase('all_partner');
            try {
              const productsResponse = await api.get(`/products?partner_id=${foundOffer.partner_id}&limit=50`);
              const rawPart = productsResponse.data || [];
              setProducts(Array.isArray(rawPart) ? rawPart : (rawPart?.products || []));
            } catch (err) {
              setProductLoadingCase('error');
            }
          } else {
            setProductLoadingCase('no_products');
            setProducts([]);
          }
          
          // Charger le partenaire
          if (hasPartnerId) {
            try {
              const partnerResponse = await api.get(`/partners/${foundOffer.partner_id}`);
              setPartner(partnerResponse.data);
            } catch (err) {
              console.error('Erreur chargement partenaire:', err);
            }
          }
        }
      } catch (err) {
        console.error('Erreur chargement preview:', err);
        setError('Impossible de charger l\'offre');
      } finally {
        setLoading(false);
      }
    };
    
    loadOffer();
  }, [id, searchParams]);
  
  // Fermer la preview
  const handleClose = () => {
    window.close();
    setTimeout(() => navigate(-1), 100);
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Chargement de la prévisualisation...</p>
        </div>
      </div>
    );
  }
  
  if (error || !offer) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <AlertTriangle className="h-12 w-12 text-orange-500 mx-auto mb-4" />
            <h2 className="text-lg font-semibold mb-2">Erreur de prévisualisation</h2>
            <p className="text-muted-foreground mb-4">{error || 'Offre introuvable'}</p>
            <Button onClick={handleClose}>Fermer</Button>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-900">
      {/* Header de la sandbox */}
      <div className="bg-gray-800 border-b border-gray-700 px-4 py-3 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Eye className="h-5 w-5 text-purple-400" />
            <div>
              <h1 className="text-white font-semibold">Prévisualisation Offre</h1>
              <p className="text-gray-400 text-sm">Mode sandbox - Navigation désactivée</p>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            {/* Toggle Desktop/Mobile */}
            <div className="flex items-center gap-1 bg-gray-700 rounded-lg p-1">
              <button
                onClick={() => setViewMode('desktop')}
                className={`p-2 rounded ${viewMode === 'desktop' ? 'bg-gray-600 text-white' : 'text-gray-400 hover:text-white'}`}
                title="Vue Desktop"
              >
                <Monitor className="h-4 w-4" />
              </button>
              <button
                onClick={() => setViewMode('mobile')}
                className={`p-2 rounded ${viewMode === 'mobile' ? 'bg-gray-600 text-white' : 'text-gray-400 hover:text-white'}`}
                title="Vue Mobile"
              >
                <Smartphone className="h-4 w-4" />
              </button>
            </div>
            
            <Button variant="outline" size="sm" onClick={handleClose} className="text-white border-gray-600 hover:bg-gray-700">
              <X className="h-4 w-4 mr-2" />
              Fermer
            </Button>
          </div>
        </div>
      </div>
      
      {/* Zone de preview - SANDBOX MODE */}
      <div className="flex justify-center p-4 sm:p-8">
        <div 
          className={`bg-white rounded-lg shadow-2xl overflow-hidden transition-all duration-300 ${
            viewMode === 'mobile' ? 'w-[375px]' : 'w-full max-w-5xl'
          }`}
        >
          {/* Barre de simulation navigateur */}
          <div className="bg-gray-100 border-b px-4 py-2 flex items-center gap-2">
            <div className="flex gap-1.5">
              <div className="w-3 h-3 rounded-full bg-red-400"></div>
              <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
              <div className="w-3 h-3 rounded-full bg-green-400"></div>
            </div>
            <div className="flex-1 bg-white rounded px-3 py-1 text-sm text-gray-500 truncate ml-2">
              gamesvisor.com/offres/{offer.id?.substring(0, 8)}...
            </div>
          </div>
          
          {/* Contenu - Composant unifié en mode preview */}
          <div className="p-6 overflow-auto max-h-[calc(100vh-200px)]">
            <RetailerOfferView
              offer={offer}
              products={products}
              partner={partner}
              mode="preview"
              productLoadingCase={productLoadingCase}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default OfferPreviewSandbox;
